import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  getThemePreference,
  resolveColorMode,
  setThemePreference,
  VISIME_THEME_CHANGED,
  VISIME_THEME_PREFERENCE_KEY,
  type ColorMode,
  type ThemePreference,
} from '../config/themeMode';
import { applyColorMode } from '../designSystem';

type ThemeModeContextValue = {
  preference: ThemePreference;
  mode: ColorMode;
  setPreference: (preference: ThemePreference) => void;
};

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

function syncDom(preference: ThemePreference): ColorMode {
  const mode = resolveColorMode(preference);
  applyColorMode(mode);
  return mode;
}

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => getThemePreference());
  const [mode, setMode] = useState<ColorMode>(() => syncDom(getThemePreference()));

  const setPreference = useCallback((next: ThemePreference) => {
    setThemePreference(next);
    setPreferenceState(next);
    setMode(syncDom(next));
  }, []);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== null && event.key !== VISIME_THEME_PREFERENCE_KEY) return;
      const next = getThemePreference();
      setPreferenceState(next);
      setMode(syncDom(next));
    };
    const onCustom = () => {
      const next = getThemePreference();
      setPreferenceState(next);
      setMode(syncDom(next));
    };
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onSystem = () => {
      if (getThemePreference() !== 'system') return;
      setMode(syncDom('system'));
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener(VISIME_THEME_CHANGED, onCustom);
    mq.addEventListener('change', onSystem);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(VISIME_THEME_CHANGED, onCustom);
      mq.removeEventListener('change', onSystem);
    };
  }, []);

  const value = useMemo(
    () => ({ preference, mode, setPreference }),
    [preference, mode, setPreference]
  );

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
}

export function useThemeMode(): ThemeModeContextValue {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) throw new Error('useThemeMode must be used within ThemeModeProvider');
  return ctx;
}
